import { useState } from 'react';
import { MapPin } from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays';
import { Input } from '~/components/ui/inputs/Input';
import { PhoneNumberInput } from '~/components/ui/inputs/PhoneNumberInput';
import { isValidPhoneNumber } from '~/components/ui/inputs/phoneNumber';
import { PortSelector } from '~/components/common/PortSelector';
import { AddressFields } from '~/components/common/AddressFields';
import { TransportModesSection } from './TransportModesSection';
import {
  useUpsertFacility,
  useAddTransportVehiclesToFacility,
  useRemoveTransportVehiclesFromFacility,
} from '~/api/facility';
import { PortType, type TransportVehicle } from '~/graphql/graphql';
import type { Facility } from './OrganizationFacilities';
import { extractGraphQLErrorMessages, type ClientError } from '~/utils';
import {
  addressToFormState,
  EMPTY_ADDRESS_FORM,
  isAddressFormComplete,
  iso3ToCountryName,
  stateCodeToRegionName,
  type AddressFormState,
} from '~/utils/address';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  allVehicles: TransportVehicle[];
  facility?: Facility;
};

type FormState = {
  code: string;
  phoneNumber: string;
  faxNumber: string;
  nearestPortId: string;
  address: AddressFormState;
};

function toFormState(facility?: Facility): FormState {
  if (!facility) {
    return {
      code: '',
      phoneNumber: '',
      faxNumber: '',
      nearestPortId: '',
      address: EMPTY_ADDRESS_FORM,
    };
  }
  return {
    code: facility.code ?? '',
    phoneNumber: facility.phoneNumber ?? '',
    faxNumber: facility.faxNumber ?? '',
    nearestPortId: facility.nearestPort?.id ?? '',
    address: addressToFormState(facility.address),
  };
}

export function UpsertFacilityModal({
  isOpen,
  onClose,
  allVehicles,
  facility,
}: Props) {
  const { t } = useTranslation('settings');
  const isEditMode = Boolean(facility);

  // Captured once per mount: handleSubmit diffs against this snapshot to tell
  // user-changed fields from untouched ones.
  const [initialForm] = useState<FormState>(() => toFormState(facility));
  const [form, setForm] = useState<FormState>(initialForm);
  const [selectedVehicleIds, setSelectedVehicleIds] = useState<string[]>(
    () => facility?.transportVehicles.map((vehicle) => vehicle.id) ?? []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Pin the saved port in case it's since been deactivated and no longer in the active list.
  const pinnedNearestPort = facility?.nearestPort
    ? { ...facility.nearestPort, type: PortType.Seaport }
    : null;

  const upsertFacility = useUpsertFacility();
  const addTransportVehicles = useAddTransportVehiclesToFacility();
  const removeTransportVehicles = useRemoveTransportVehiclesFromFacility();

  function handleField(name: string, value: string) {
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function handleVehicleToggle(
    vehicleId: string,
    isCurrentlySelected: boolean
  ) {
    setSelectedVehicleIds((prev) =>
      isCurrentlySelected
        ? prev.filter((id) => id !== vehicleId)
        : [...prev, vehicleId]
    );
  }

  function handleClose() {
    setForm(toFormState(facility));
    setSelectedVehicleIds(
      facility?.transportVehicles.map((vehicle) => vehicle.id) ?? []
    );
    setIsSubmitting(false);
    onClose();
  }

  const isPhoneValid = isValidPhoneNumber(form.phoneNumber);
  const isFaxValid = isValidPhoneNumber(form.faxNumber);
  const isValid =
    isAddressFormComplete(form.address, true) && isPhoneValid && isFaxValid;

  async function handleSubmit() {
    if (!isValid) return;
    setIsSubmitting(true);

    try {
      // Normalize country/region to display names only when the user changed
      // them; untouched fields pass through verbatim so the backend's
      // address-changed check stays false (a stored "BC" must not become
      // "British Columbia" and trigger re-geocoding on unrelated edits).
      const countryName =
        facility && form.address.country === initialForm.address.country
          ? facility.address.country
          : iso3ToCountryName(form.address.country);
      const regionName =
        facility && form.address.region === initialForm.address.region
          ? (facility.address.region ?? null)
          : stateCodeToRegionName(form.address.country, form.address.region);

      const result = await upsertFacility.mutateAsync({
        id: facility?.id,
        input: {
          code: form.code.trim() || null,
          phoneNumber: form.phoneNumber.trim() || null,
          faxNumber: form.faxNumber.trim() || null,
          addressLine1: form.address.addressLine1.trim(),
          addressLine2: form.address.addressLine2.trim() || null,
          city: form.address.city.trim(),
          region: regionName,
          zipCode: form.address.zipCode.trim(),
          country: countryName,
          nearestPortId: form.nearestPortId || null,
        },
      });

      const facilityId = result.upsertFacility.id;
      const initialIds =
        facility?.transportVehicles.map((vehicle) => vehicle.id) ?? [];
      const toAdd = selectedVehicleIds.filter((id) => !initialIds.includes(id));
      const toRemove = initialIds.filter(
        (id) => !selectedVehicleIds.includes(id)
      );

      if (toAdd.length > 0) {
        await addTransportVehicles.mutateAsync({
          facilityId,
          transportVehicleIds: toAdd,
        });
      }
      if (toRemove.length > 0) {
        await removeTransportVehicles.mutateAsync({
          facilityId,
          transportVehicleIds: toRemove,
        });
      }

      toast.success(
        isEditMode
          ? t('settings.facilities.update_success')
          : t('settings.facilities.create_success')
      );
      handleClose();
    } catch (error: unknown) {
      toast.error(extractGraphQLErrorMessages(error as ClientError).join(', '));
      setIsSubmitting(false);
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="bg-neut-bg-alt rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-prim-100 rounded-lg">
            <MapPin size={20} className="text-prim" />
          </div>
          <h2 id="Modal-title" className="text-lg font-semibold text-neut-prim">
            {isEditMode
              ? t('settings.facilities.edit_modal.title')
              : t('settings.facilities.add_modal.title')}
          </h2>
        </div>

        {facility && facility.locations.length > 0 && (
          <p
            className="text-sm text-warning-700 bg-warning-50 border border-warning-300 rounded-lg px-3 py-2 mb-4"
            data-testid="shared-address-note"
          >
            {t('settings.facilities.edit_modal.shared_address_note', {
              count: facility.locations.length,
            })}
          </p>
        )}

        <div className="space-y-4">
          <Input
            label={t('settings.facilities.add_modal.code')}
            name="code"
            value={form.code}
            onChange={handleField}
            placeholder="e.g., MTL-01"
          />
          <AddressFields
            value={form.address}
            onChange={(address) => setForm((prev) => ({ ...prev, address }))}
          />
          <PhoneNumberInput
            label={t('settings.facilities.add_modal.phone_label')}
            extensionLabel={t('settings.facilities.add_modal.extension_label')}
            name="phoneNumber"
            value={form.phoneNumber}
            onChange={handleField}
            error={
              isPhoneValid
                ? undefined
                : t('settings.facilities.add_modal.phone_invalid')
            }
            dataCy="facility-phone-input"
          />
          <PhoneNumberInput
            label={t('settings.facilities.add_modal.fax_label')}
            extensionLabel={t('settings.facilities.add_modal.extension_label')}
            name="faxNumber"
            value={form.faxNumber}
            onChange={handleField}
            error={
              isFaxValid
                ? undefined
                : t('settings.facilities.add_modal.fax_invalid')
            }
            dataCy="facility-fax-input"
          />

          <div>
            <PortSelector
              label={t('settings.facilities.add_modal.nearest_port')}
              name="nearestPortId"
              value={form.nearestPortId}
              onChange={handleField}
              pinned={pinnedNearestPort}
              formatLabel={(name) =>
                t('settings.facilities.port_name', { name })
              }
            />
            <p className="text-xs text-neut-sec mt-1">
              {t('settings.facilities.add_modal.nearest_port_description')}
            </p>
          </div>

          {allVehicles.length > 0 && (
            <TransportModesSection
              allVehicles={allVehicles}
              selectedVehicleIds={selectedVehicleIds}
              onToggle={handleVehicleToggle}
              pendingVehicleIds={new Set()}
            />
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-4 py-2 text-sm font-medium text-neut-prim border border-neut-200 rounded-lg hover:bg-neut-50 transition-colors"
            >
              {t('settings.facilities.add_modal.cancel')}
            </button>
            <button
              type="button"
              disabled={!isValid || isSubmitting}
              onClick={handleSubmit}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-prim rounded-lg hover:bg-prim-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isEditMode
                ? t('settings.facilities.edit_modal.submit')
                : t('settings.facilities.add_modal.submit')}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
